import { useEffect, useRef } from "react";

/**
 * Lightweight SEO helper — updates <title>, meta description, canonical, OG/Twitter,
 * and JSON-LD on route change without requiring react-helmet.
 *
 * Cleans up its injected JSON-LD script on unmount / when path changes so stale
 * structured data doesn't linger across SPA navigation.
 */
const SITE_URL = "https://lyceumnorthernluzon.edu.ph";
const DEFAULT_IMG = "https://lyceumnorthernluzon.edu.ph/images/1920/14104567/1000010347.jpg";
const JSONLD_ATTR = "data-jsonld-page";

function upsertMeta(attrName, attrValue, contentValue) {
    if (!contentValue) return;
    let el = document.head.querySelector(`meta[${attrName}="${attrValue}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
    }
    el.setAttribute("content", contentValue);
}

function upsertLink(rel, href) {
    if (!href) return;
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
    }
    el.setAttribute("href", href);
}

function removeAllPageJsonLd() {
    document.head.querySelectorAll(`script[${JSONLD_ATTR}]`).forEach((n) => n.remove());
}

function injectPageJsonLd(obj) {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute(JSONLD_ATTR, "1");
    el.textContent = JSON.stringify(obj);
    document.head.appendChild(el);
    return el;
}

export default function SEO({
    title,
    description,
    path = "",
    image = DEFAULT_IMG,
    type = "website",
    schema = null,
}) {
    const schemaJson = schema ? JSON.stringify(schema) : "";
    const injectedRef = useRef(null);

    useEffect(() => {
        const fullTitle = title
            ? `${title} | Lyceum Northern Luzon`
            : "Lyceum Northern Luzon — Urdaneta City, Pangasinan";
        document.title = fullTitle;

        const url = `${SITE_URL}${path || "/"}`;
        upsertMeta("name", "description", description);
        upsertLink("canonical", url);
        upsertMeta("property", "og:type", type);
        upsertMeta("property", "og:site_name", "Lyceum Northern Luzon");
        upsertMeta("property", "og:title", fullTitle);
        upsertMeta("property", "og:description", description || "");
        upsertMeta("property", "og:url", url);
        upsertMeta("property", "og:image", image);
        upsertMeta("name", "twitter:card", "summary_large_image");
        upsertMeta("name", "twitter:title", fullTitle);
        upsertMeta("name", "twitter:description", description || "");
        upsertMeta("name", "twitter:image", image);

        // Always clear any previous page-level JSON-LD before injecting new
        removeAllPageJsonLd();
        injectedRef.current = null;
        if (schemaJson) {
            try {
                injectedRef.current = injectPageJsonLd(JSON.parse(schemaJson));
            } catch { /* ignore malformed schema */ }
        }

        return () => {
            // On route change / unmount: strip only the JSON-LD we injected
            removeAllPageJsonLd();
            injectedRef.current = null;
        };
    }, [title, description, path, image, type, schemaJson]);

    return null;
}

export function makeBreadcrumbSchema(items) {
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: items.map((it, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: it.name,
            item: it.url ? `${SITE_URL}${it.url}` : undefined,
        })),
    };
}
